import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, GripVertical, AlertCircle } from 'lucide-react';

import { API_URL } from '../../config/api';

const MAX_SERVICES = 12;
const CHAR_LIMITS = { title: 50, description: 250 };

// Available Lucide icons for services
const AVAILABLE_ICONS = [
    'stethoscope', 'dna', 'layers', 'baby', 'heart-pulse', 'pill',
    'microscope', 'activity', 'clipboard', 'users', 'file-text', 'shield'
];

const ServicesManager = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [meta, setMeta] = useState({ active: 0, can_add_more: true });
    const [editingService, setEditingService] = useState(null);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => { fetchServices(); }, []);

    const getImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `${API_URL}${url}`;
    };

    const fetchServices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/site-services/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setServices(data.data || []);
                setMeta(data.meta || {});
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al cargar servicios' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (service) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const isNew = !service.id;
            let imageUrl = service.image_url || null;

            // Upload image first (if any), then send JSON update/create.
            if (service.imageFile) {
                const uploadForm = new FormData();
                uploadForm.append('file', service.imageFile);
                uploadForm.append('category', 'services');

                const uploadResponse = await fetch(`${API_URL}/upload`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: uploadForm
                });
                const uploadData = await uploadResponse.json();
                if (!uploadData.success) {
                    setMessage({ type: 'error', text: uploadData.message || 'Error al subir imagen' });
                    setSaving(false);
                    return;
                }
                imageUrl = uploadData.data?.url || null;
            }

            const payload = {
                title: service.title,
                description: service.description,
                icon: service.icon,
                is_active: !!service.is_active,
                image_url: imageUrl
            };
            if (service.display_order !== undefined && service.display_order !== null && service.display_order !== '') {
                payload.display_order = service.display_order;
            }

            const url = isNew ? `${API_URL}/site-services` : `${API_URL}/site-services/${service.id}`;
            const response = await fetch(url, {
                method: isNew ? 'POST' : 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Guardado' });
                fetchServices();
                setShowForm(false);
                setEditingService(null);
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este servicio?')) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/site-services/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchServices();
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al eliminar' });
        }
    };

    const CharCounter = ({ value, limit }) => {
        const count = (value || '').length;
        const pct = (count / limit) * 100;
        return <span className={`char-counter ${pct > 90 ? 'warning' : ''}`}>{count}/{limit}</span>;
    };

    const ServiceForm = ({ service, onSave, onCancel }) => {
        const [formData, setFormData] = useState(service || {
            title: '', description: '', icon: 'activity', is_active: true, image_url: null
        });
        const [preview, setPreview] = useState(service?.image_url || null);

        const handleImageChange = (e) => {
            const file = e.target.files[0];
            if (file) {
                setFormData({ ...formData, imageFile: file });
                setPreview(URL.createObjectURL(file));
            }
        };

        return (
            <div className="item-form">
                <div className="form-grid">
                    <div className="form-group">
                        <label>Título <CharCounter value={formData.title} limit={CHAR_LIMITS.title} /></label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value.slice(0, CHAR_LIMITS.title) })}
                            placeholder="Nombre del servicio"
                        />
                    </div>

                    <div className="form-group">
                        <label>Imagen (Opcional)</label>
                        <div className="flex gap-4 items-center">
                            {preview && (
                                <img src={preview.startsWith('blob:') ? preview : getImageUrl(preview)} alt="Preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
                            )}
                            <input type="file" accept="image/*" onChange={handleImageChange} />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Reemplaza el ícono si se sube. Máx 2MB.</p>
                    </div>

                    <div className="form-group">
                        <label>Ícono (Si no hay imagen)</label>
                        <select value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })}>
                            {AVAILABLE_ICONS.map(icon => (
                                <option key={icon} value={icon}>{icon}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group full-width">
                        <label>Descripción <CharCounter value={formData.description} limit={CHAR_LIMITS.description} /></label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, CHAR_LIMITS.description) })}
                            rows={3}
                            placeholder="Descripción del servicio"
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                            Visible en el sitio
                        </label>
                    </div>
                </div>
                <div className="form-actions">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
                    <button className="btn btn-primary" onClick={() => onSave(formData)} disabled={saving || !formData.title}>
                        <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return <div className="loading-state">Cargando servicios...</div>;

    return (
        <div className="services-manager">
            <div className="manager-header">
                <div className="header-info">
                    <h3>Servicios del Sitio</h3>
                    <span className="count-badge">{meta.active || 0} / {MAX_SERVICES} activos</span>
                </div>
                <button className="btn btn-primary" onClick={() => { setEditingService(null); setShowForm(true); }} disabled={!meta.can_add_more}>
                    <Plus size={16} /> Agregar Servicio
                </button>
            </div>

            {!meta.can_add_more && (
                <div className="limit-warning">
                    <AlertCircle size={16} /> Límite de {MAX_SERVICES} servicios activos alcanzado.
                </div>
            )}

            {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
            {showForm && <ServiceForm service={editingService} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingService(null); }} />}

            <div className="services-list">
                {services.map((service) => (
                    <div key={service.id} className={`service-card ${!service.is_active ? 'inactive' : ''}`}>
                        <div className="drag-handle"><GripVertical size={16} /></div>
                        <div className="service-icon">
                            {service.image_url ? (
                                <img src={getImageUrl(service.image_url)} alt={service.title} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                            ) : (
                                <span>{service.icon ? '📦' : '🔬'}</span>
                            )}
                        </div>
                        <div className="service-info">
                            <h4>{service.title}</h4>
                            <p>{service.description}</p>
                            {!service.is_active && <span className="inactive-badge">Oculto</span>}
                        </div>
                        <div className="service-actions">
                            <button onClick={() => { setEditingService(service); setShowForm(true); }}>✏️</button>
                            <button onClick={() => handleDelete(service.id)} className="danger"><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ServicesManager;
